package com.elan.edubridge.ui.member

import com.elan.edubridge.shared.domain.model.SessionState
import com.elan.edubridge.ui.shell.escapeHtml
import com.elan.edubridge.ui.shell.icon
import kotlinx.browser.document
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.launch
import org.w3c.dom.HTMLButtonElement
import org.w3c.dom.HTMLElement

/**
 * `/app/me/notifications` — 필터 칩(전체/출결/공지/과제) + 리스트(가이드 §3.8).
 *
 * 읽음 처리는 실 Firebase 전환 후 `readAt`만 갱신하는 계약(rules:58-60)을 그대로 흉내내 지금은
 * [MemberNotifications]의 InMemory 상태만 바꾼다. 필터 칩 클릭은 재조회 없이 이미 불러온 목록을
 * 다시 그리기만 한다(전체/출결/공지/과제 전환마다 3개 조회를 반복하지 않기 위함).
 */
object NotificationInboxPage {
    private val scope = MainScope()

    fun render(session: SessionState) {
        scope.launch {
            val context = loadMemberContext(session)
            val items = MemberNotifications.loadAll(context)
            show(context, items, selectedCategory = null)
        }
    }

    private suspend fun show(context: MemberContext, items: List<MemberNotificationItem>, selectedCategory: NotificationCategory?) {
        val userId = context.session.account?.id.orEmpty()
        MemberShell.render(context, MemberShell.Tab.NOTIFICATIONS, body(items, selectedCategory, userId))
        wireChips(context, items)
        wireItemClicks(userId)
    }

    private fun body(items: List<MemberNotificationItem>, selectedCategory: NotificationCategory?, userId: String): String {
        val chipsHtml = buildChips(selectedCategory)
        val filtered = if (selectedCategory == null) items else items.filter { it.category == selectedCategory }

        val listHtml = if (filtered.isEmpty()) {
            """
            <div class="eb-empty-state">
                ${icon("icon-notification", "xl")}
                <h2>아직 알림이 없어요</h2>
                <p>등원·하원, 공지, 과제 소식이 오면 여기에 모여요.</p>
            </div>
            """.trimIndent()
        } else {
            filtered.groupBy { it.groupLabel }.entries.joinToString("") { (group, groupItems) ->
                val rows = groupItems.joinToString("") { itemHtml(it, userId) }
                """<h2 class="eb-notifications__group-label">${group.escapeHtml()}</h2><ol class="eb-notification-list">$rows</ol>"""
            }
        }

        return """
            <h1 class="eb-page-title">알림함</h1>
            <div class="eb-filter-chips" role="tablist" aria-label="알림 종류">$chipsHtml</div>
            <div id="notification-list-region">$listHtml</div>
        """.trimIndent()
    }

    private fun buildChips(selectedCategory: NotificationCategory?): String {
        val options: List<NotificationCategory?> = listOf(null) + NotificationCategory.entries
        return options.joinToString("") { category ->
            val active = category == selectedCategory
            val label = category?.label ?: "전체"
            """<button type="button" class="eb-filter-chip${if (active) " eb-filter-chip--active" else ""}" role="tab" aria-selected="$active" data-category="${category?.name.orEmpty()}">$label</button>"""
        }
    }

    private fun itemHtml(item: MemberNotificationItem, userId: String): String {
        val unread = !MemberNotifications.isRead(userId, item.id)
        val unreadClass = if (unread) " eb-notification-item--unread" else ""
        val ctaHtml = item.linkLabel?.let {
            """<a class="eb-button eb-button--ghost eb-button--sm eb-notification-item__cta" href="${item.linkHref}" data-notification-id="${item.id}">${it.escapeHtml()} →</a>"""
        }.orEmpty()

        return """
            <li class="eb-notification-item$unreadClass">
                <a class="eb-notification-item__link" href="${item.linkHref}" data-notification-id="${item.id}">
                    <span class="eb-notification-item__icon eb-notification-item__icon--${item.iconVariant}">${icon(item.iconId, "md")}</span>
                    <div class="eb-notification-item__body">
                        <strong>${item.title.escapeHtml()}</strong>
                        <p>${item.body.escapeHtml()}</p>
                        <span class="eb-meta">${item.timeLabel.escapeHtml()}</span>
                    </div>
                </a>
                $ctaHtml
                ${if (unread) """<span class="eb-badge-dot" aria-label="읽지 않음"></span>""" else ""}
            </li>
        """.trimIndent()
    }

    private fun wireChips(context: MemberContext, items: List<MemberNotificationItem>) {
        val chips = document.querySelectorAll(".eb-filter-chip")
        for (index in 0 until chips.length) {
            val chip = chips.item(index) as? HTMLButtonElement ?: continue
            chip.onclick = {
                val categoryName = chip.getAttribute("data-category").orEmpty()
                val category = NotificationCategory.entries.firstOrNull { it.name == categoryName }
                scope.launch { show(context, items, category) }
                null
            }
        }
    }

    /** 알림 항목(본문 링크 + 보조 CTA)을 눌렀을 때 이동 전에 읽음으로 표시한다 — 실제 이동은
     * `Router`의 전역 클릭 가로채기(`ui/shell/Router.kt`)가 그대로 처리하므로 여기서는
     * `preventDefault`를 호출하지 않는다. */
    private fun wireItemClicks(userId: String) {
        val links = document.querySelectorAll("[data-notification-id]")
        for (index in 0 until links.length) {
            val link = links.item(index) as? HTMLElement ?: continue
            val id = link.getAttribute("data-notification-id") ?: continue
            link.addEventListener("click", { MemberNotifications.markRead(userId, id) })
        }
    }
}
